import { computed, onMounted, reactive, ref, provide, inject } from 'vue'
import { useStore } from 'vuex'
import { useShop } from '@b2ag/catalog/src/useShop'
import { useIndexedDB } from '@b2ag/utils/src/indexedDB/useIndexedDB'
import { useUser } from '@b2ag/user/src/useUser'
import { computeProductNeeds } from '../calculator.business'

const collectionName = 'calculator'

export function getProviderName(productId) {
  return `calculator-${productId}`
}

export function useCalculator(productId): any {
  return inject(getProviderName(productId))
}

export function provideCalculator(productID) {
  const { packagingOptions } = useShop(productID)
  const { getters } = useStore()
  const { getEntry, setEntry, putEntry } = useIndexedDB(collectionName)
  const { profile: user } = useUser()

  const entryID = `${user.value.id}__${productID}`

  const packagingList = Array.from(packagingOptions.values())
  const availableUnits = {
    MG: packagingList.some((option) => option.unit === 'MG'),
    kg: packagingList.some((option) => option.unit === 'kg'),
  }

  const defaultCalculatorState: {
    unit: 'MG' | 'kg'
    density: number | undefined
    pmg: number | undefined
    surface: number | undefined
  } = {
    unit: 'kg',
    /** grains per m² */ density: undefined,
    /** g per 1000 grains */ pmg: undefined,
    /** hectares = 10 000 m² */ surface: undefined,
  }

  defaultCalculatorState.unit = availableUnits.MG ? 'MG' : 'kg'

  const state = ref({ ...defaultCalculatorState })

  const setupCalculatorState = (userCalculatorState) => {
    const existingState = userCalculatorState?.calculatorState
    state.value = reactive(existingState || { ...defaultCalculatorState })
    if (getters.isAuthenticated) setEntry({ id: entryID, calculatorState: state.value })
  }

  const updateCalculatorDB = () => {
    if (getters.isAuthenticated) putEntry({ id: entryID, calculatorState: state.value })
  }

  const reset = () => {
    Object.assign(state.value, defaultCalculatorState)
    if (getters.isAuthenticated) putEntry({ id: entryID, calculatorState: state.value })
  }

  onMounted(async () => {
    const userCalculatorState = getters.isAuthenticated ? await getEntry(entryID) : {}
    setupCalculatorState(userCalculatorState)
  })

  const result = computed(() => computeProductNeeds(state.value.surface, state.value.density, state.value.pmg))
  const hasPositiveConvertResult = computed(() => !!result.value[state.value.unit])

  const calculator = {
    state,
    availableUnits,
    packagingOptions,
    result,
    hasPositiveConvertResult,
    reset,
    updateCalculatorDB,
  }

  provide(getProviderName(productID), calculator)
  return calculator
}
